import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { routerActions } from '../router/index.js';

export interface ShareConfig {
    itemIds?: string[];
    myPlaybook?: boolean;
}

interface VideoDownload {
    playbookId: string;
    playId: string;
    programId: string;
    nonce: string;
    estimatedWait: number;
    name: string;
    previewUrl?: string;
    videoUrl?: string;
    error?: boolean;
    linkId?: string;
}

export interface PlaybookState {
    teamPlaybookId: string;

    shareConfig: ShareConfig;
    viewershipProfile: any[];

    renameItemId: string;

    publicLoadPlayError: boolean;

    printPlay: boolean;

    overrideMenuWithBack: boolean;

    videoDownloads: VideoDownload[];
    manualVideoDownload: boolean;
    shareModifications: any;

    creatingPlay: boolean;
}

const playbookDefaultState: PlaybookState = {
    teamPlaybookId: undefined,
    shareConfig: undefined,
    viewershipProfile: undefined,
    renameItemId: undefined,
    publicLoadPlayError: false,
    printPlay: false,
    overrideMenuWithBack: false,
    videoDownloads: [],
    manualVideoDownload: false,
    shareModifications: undefined,
    creatingPlay: false,
};

const { reducer: playbookReducer, actions: playbookActions } = createSlice({
    name: 'playbook',
    initialState: playbookDefaultState,
    reducers: {
        SetRenameItemId: (state, action: PayloadAction<string | null>) => {
            state.renameItemId = action.payload;
        },

        SetSharing: {
            prepare: (payload?: ShareConfig) => ({
                payload,
            }),
            reducer: (state, action: PayloadAction<ShareConfig>) => {
                state.shareConfig = action.payload;
            },
        },

        Printing: (state, action) => {
            state.printPlay = !!action.payload;
        },

        PublicLoadPlayError: (state, action) => {
            state.publicLoadPlayError = action.payload;
        },

        OverrideMenuWithBack: (state, action: PayloadAction<boolean | null>) => {
            state.overrideMenuWithBack = action.payload;
        },

        SetManualVideoDownload: (state, action: PayloadAction<boolean>) => {
            state.manualVideoDownload = action.payload;
        },

        AddVideoDownload: (state, action: PayloadAction<VideoDownload>) => {
            // If a download already exists for this play, don't duplicate it
            const existingDownloads = state.videoDownloads.filter(
                (vd) =>
                    vd.playId !== action.payload.playId ||
                    vd.playbookId !== action.payload.playbookId ||
                    vd.programId !== action.payload.programId
            );
            state.videoDownloads = [...existingDownloads, action.payload];
        },

        RemoveVideoDownload: (state, action: PayloadAction<VideoDownload>) => {
            if (action.payload) {
                state.videoDownloads = state.videoDownloads.filter(
                    (vd) =>
                        vd.playId !== action.payload.playId ||
                        vd.playbookId !== action.payload.playbookId ||
                        vd.programId !== action.payload.programId
                );
            }
        },

        ViewershipProfileLoaded: (state, action: PayloadAction<any[] | null>) => {
            state.viewershipProfile = action.payload;
        },

        SetShareModifications: (state, action: PayloadAction<any>) => {
            state.shareModifications = action.payload;
        },

        SetCreatingPlay: (state, action: PayloadAction<boolean>) => {
            state.creatingPlay = action.payload;
        },
    },
    extraReducers: (builder) =>
        builder.addCase(routerActions.SetActiveTeam, (state) => {
            state.shareConfig = null;
        }),
});

export { playbookReducer, playbookActions, playbookDefaultState };
